import { BaseService } from './BaseService';
import { apiService } from './apiService';
import { SignalRNegotiateResponse } from '../types/api';
import { userService } from './userService';
import { demoService } from './demoService';
import { HubConnection, HubConnectionBuilder, HubConnectionState, LogLevel } from '@microsoft/signalr';

interface JoinSessionResponse {
  group: string;
}

interface SignalRDebugInfo {
  connectionState: string;
  isConnected: boolean;
  isInitialized: boolean;
  eventHandlers: string[];
  reconnectAttempts: number;
  setupInProgress: boolean;
  connectionId?: string;
  transport?: string;
  baseUrl?: string;
}

export class SignalRService extends BaseService {
  private baseUrl: string;
  private connection: HubConnection | null = null;
  private connectionState: string = 'Disconnected';
  private eventHandlers = new Map<string, Set<Function>>();
  private isInitialized: boolean = false;
  private reconnectAttempts: number = 0;
  private maxReconnectAttempts: number = 5;
  private setupInProgress: boolean = false;
  private userToken: string | null = null;
  private userId: string | null = null;
  private handlersRegistered: boolean = false;
  private addDynamicHandler?: (methodName: string) => void;

  constructor() {
    super('SignalRService', { debug: false });
    this.baseUrl = process.env.REACT_APP_BACKEND_API_URL || '';
  }

  /**
   * Get the active base URL (demo or regular based on mode)
   */
  private getActiveBaseUrl(): string {
    if (demoService.isDemoModeActive()) {
      return demoService.getDemoBackendUrl();
    }
    return this.baseUrl;
  }

  /**
   * Get SignalR connection information
   */
  async negotiate(): Promise<SignalRNegotiateResponse> {
    return this.executeOperation(async () => {
      return await apiService.get<SignalRNegotiateResponse>('/api/negotiate');
    }, 'negotiate');
  }

  protected onCircuitBreakerChange(eventType: string, data: any): void {
    this.logDebug(`Circuit breaker event: ${eventType}`, data);
  }

  protected onRetry(error: Error, attempt: number, delay: number, operationName: string): void {
    this.logDebug(`Retrying ${operationName}`, { 
      attempt, 
      delay, 
      error: error.message 
    });
  }

  /* ------------------------------------------------------------------------
     Helper Methods
  /* ------------------------------------------------------------------------
     Public API
  ------------------------------------------------------------------------ */

  /**
   * Initializes the underlying HubConnection (if not already connected).
   * @param accessToken - auth token
   * @param userId - user ID for group registration
   */
  async initialize(accessToken: string, userId: string): Promise<void> {
    // If already initialized and connected, don't initialize again
    if (this.isInitialized && this.isConnected()) {
      return;
    }

    // If currently connecting or setting up, wait for it to complete
    if (this.connectionState === 'Connecting' || this.setupInProgress) {
      return;
    }

    // Store the token and user ID for potential use in other methods
    this.userToken = accessToken;
    this.userId = userId;

    try {
      await this.setupConnection();
      this.isInitialized = true;
    } catch (error) {
      console.error('[SignalR] SignalR service initialization failed:', error);
      throw error;
    }
  }

  /** Joins the user's general session by calling the backend REST endpoint. */
  async joinUserSession(userId: string): Promise<void> {
    if (!this.isConnected()) throw new Error('SignalR connection not established');

    try {
      const data = await apiService.post<JoinSessionResponse>('/api/chat/join', {});

      // Register dynamic handler for the provided user ID
      if (userId && this.addDynamicHandler) {
        this.addDynamicHandler(`user-${userId}`);
      } else {
        console.warn('[SignalR] No user ID provided for dynamic handler registration');
      }

      this.emit('joinedUserSession', { groupName: data.group });
    } catch (err: any) {
      console.error('[SignalR] joinUserSession error', { error: err.message, stack: err.stack });
      this.emit('error', err);
      throw err;
    }
  }

  /** Terminates the connection. */
  async disconnect(): Promise<void> {
    if (this.connection) {
      try {
        await this.connection.stop();
      } catch (err) {
        console.warn('[SignalR] Error stopping connection:', err);
      }
      this.connection = null;
      this.isInitialized = false;
      this.connectionState = 'Disconnected';
      this.setupInProgress = false;
      this.emit('connectionStateChanged', this.connectionState);
    }
  }

  isConnected(): boolean {
    return this.connection?.state === HubConnectionState.Connected;
  }

  getConnectionState(): string {
    return this.connectionState;
  }

  getReconnectAttempts(): number {
    return this.reconnectAttempts;
  }

  /**
   * Registers a dynamic handler for user-specific methods
   * This is useful for handling user-${userId} method calls
   */
  registerUserHandler(userId: string): void {
    if (this.addDynamicHandler) {
      const methodName = `user-${userId}`;
      this.addDynamicHandler(methodName);
    }
  }

  /* ------------------------------------------------------------------------
     Private helpers
  ------------------------------------------------------------------------ */

  private async setupConnection(): Promise<void> {
    // If already connected or connecting, don't setup again
    if (this.connection && (this.isConnected() || this.connectionState === 'Connecting')) {
      return;
    }

    // If setup is already in progress, don't start another one
    if (this.setupInProgress) {
      return;
    }

    // Clean up existing connection completely before creating new one
    if (this.connection) {
      try {
        // Remove ALL existing event handlers to prevent accumulation
        // Note: SignalR's off() without parameters removes all handlers for all events
        const existingConnection = this.connection;
        
        // Remove handlers for all registered events
        ['status', 'stream', 'complete', 'updateSession', 'errorUpdate', 'activeRunsBlocked', 'runsStatus'].forEach(eventName => {
          try {
            existingConnection.off(eventName);
          } catch (e) {
            // Ignore individual event cleanup errors
          }
        });
        
        await this.connection.stop();
      } catch (err) {
        console.warn('[SignalR] Error cleaning up existing connection:', err);
      }
      this.connection = null;
      this.handlersRegistered = false; // Force re-registration with clean slate
    }

    this.setupInProgress = true;
    try {
      this.setState('Connecting');

      const { url, accessToken: signalRToken } = await userService.negotiate(this.userToken!);
      
      if (!url || !signalRToken) throw new Error('Invalid negotiate response');

      // Reset handlers registered flag since we're creating a new connection
      this.handlersRegistered = false;

      this.connection = new HubConnectionBuilder()
        .withUrl(url, {
          accessTokenFactory: () => {
            // console.log('[SignalR] accessTokenFactory called, returning token of length:', signalRToken?.length);
            return signalRToken;
          }
        })
        .withAutomaticReconnect({
          nextRetryDelayInMilliseconds: ctx => {
            const delay = ctx.previousRetryCount < this.maxReconnectAttempts ? 
              Math.min(1000 * 2 ** ctx.previousRetryCount, 30000) : null;
            return delay;
          }
        })
        .configureLogging(LogLevel.Error)
        .build();

      // Setup handlers BEFORE starting the connection to prevent timing issues
      this.setupConnectionHandlers();
      this.setupMessageHandlers();

      await this.connection.start();
      this.setState('Connected');
      this.emit('connected');

      // Automatically join user session after connection
      if (this.userId) {
        await this.joinUserSession(this.userId);
      }
    } catch (err: any) {
      console.error('[SignalR] Connection setup failed', { error: err.message, stack: err.stack });
      this.setState('Failed');
      this.emit('error', err);
      throw err;
    } finally {
      this.setupInProgress = false;
    }
  }

  private setupConnectionHandlers(): void {
    if (!this.connection) return;

    this.connection.onclose(err => {
      this.setState('Disconnected');
      if (err) this.emit('error', err);
    });

    this.connection.onreconnecting(err => {
      this.setState('Reconnecting');
      this.reconnectAttempts += 1;
      if (err) this.emit('reconnecting', err);
    });

    this.connection.onreconnected(() => {
      this.setState('Connected');
      this.reconnectAttempts = 0;

      // Don't re-setup message handlers on reconnection since they should persist
      // Only rejoin sessions
      this.emit('reconnected');
      this.rejoinAllSessions();
    });
  }

  private async rejoinAllSessions(): Promise<void> {
    // Rejoin user session
    if (this.userId) {
      try {
        await this.joinUserSession(this.userId);
      } catch (err: any) {
        console.warn('[SignalR] Re‑join user session failed', { error: err.message });
      }
    }
  }

  // Fixed: Single handler per normalized event type to prevent duplicates
  private setupMessageHandlers(): void {
    if (!this.connection || this.handlersRegistered) return;

    // Import the event mappings from config
    import('../config/signalRConfig').then(({ SIGNALR_EVENT_MAPPINGS }) => {
      if (!this.connection) return;

      // Group events by their normalized type to avoid duplicate handlers
      const normalizedHandlers = new Map<string, string[]>();
      
      Object.entries(SIGNALR_EVENT_MAPPINGS).forEach(([eventName, normalizedType]) => {
        if (!normalizedHandlers.has(normalizedType)) {
          normalizedHandlers.set(normalizedType, []);
        }
        normalizedHandlers.get(normalizedType)!.push(eventName);
      });

      // Register ONE handler per normalized type that listens to ALL variants
      normalizedHandlers.forEach((eventVariants, normalizedType) => {
        // Create a single handler for this normalized type
        const handler = (data: any) => {
          // Emit the normalized event - SignalRManager will listen to this
          this.emit(normalizedType, data);
        };

        // Register the same handler for all variants of this normalized type
        eventVariants.forEach(eventName => {
          this.connection!.on(eventName, handler);
        });
      });

      // Add a catch-all handler to see what other events might be coming through
      this.connection!.onclose((error) => {
        if (error) console.log(`[SignalR] Connection closed:`, error);
      });

      this.connection!.onreconnecting((error) => {
        if (error) console.log(`[SignalR] Reconnecting:`, error);
      });

      this.connection!.onreconnected((connectionId) => {
        console.log(`[SignalR] Reconnected with connectionId:`, connectionId);
      });

      // Keep dynamic handler for specialized user-specific methods only
      const dynamicEventHandlers = new Map<string, Function>();
      
      this.addDynamicHandler = (methodName: string) => {
        if (!dynamicEventHandlers.has(methodName) && this.connection) {
          const handler = (data: any) => {
            this.emit('dynamicEvent', { method: methodName, data });
          };

          this.connection.on(methodName, handler);
          dynamicEventHandlers.set(methodName, handler);
        }
      };

      this.handlersRegistered = true;
    });
  }

  private setState(s: string): void {
    this.connectionState = s;
    this.emit('connectionStateChanged', s);
  }

  /**
   * Get detailed connection info for debugging
   */
  getDebugInfo(): SignalRDebugInfo {
    return {
      connectionState: this.connectionState,
      isConnected: this.isConnected(),
      isInitialized: this.isInitialized,
      eventHandlers: Array.from(this.eventHandlers.keys()),
      reconnectAttempts: this.reconnectAttempts,
      setupInProgress: this.setupInProgress,
      connectionId: this.connection?.connectionId || undefined,
      transport: (this.connection as any)?.transport?.name,
      baseUrl: this.getActiveBaseUrl()
    };
  }

  /* ------------------------------------------------------------------------
     Simple pub/sub (on / off / emit)
  ------------------------------------------------------------------------ */
  on(event: string, handler: Function): void {
    if (!this.eventHandlers.has(event)) this.eventHandlers.set(event, new Set());
    this.eventHandlers.get(event)!.add(handler);
  }

  off(event: string, handler: Function): void {
    this.eventHandlers.get(event)?.delete(handler);
  }

  emit(event: string, data?: any): void {
    this.eventHandlers.get(event)?.forEach(h => {
      try { 
        h(data); 
      } catch (e) {
        console.error(`[SignalR] Error in event handler for ${event}:`, e);
      }
    });
  }
}

export const signalRService = new SignalRService();
